import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { 
  Loader2,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  AlertCircle,
  Archive,
  Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const AdminContacts = () => {
  const { api } = useAdmin();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    loadContacts();
  }, [filter]);

  const loadContacts = async () => {
    try {
      const status = filter === 'all' ? null : filter;
      const response = await api.getContacts(status);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      toast.error('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      await api.updateContactStatus(contactId, status);
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, status } : c
      ));
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-orange-100 text-orange-700';
      case 'read': return 'bg-blue-100 text-blue-700';
      case 'replied': return 'bg-green-100 text-green-700';
      case 'archived': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
          <p className="text-gray-600">Manage customer inquiries</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {['all', 'new', 'read', 'replied', 'archived'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            onClick={() => setFilter(status)}
            className={filter === status ? 'bg-purple-600' : ''}
            size="sm"
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {/* Contacts List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {contacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No messages found
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {contacts.map((contact) => (
              <div key={contact.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold flex-shrink-0">
                      {contact.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{contact.name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                          {contact.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {contact.email}
                        </span>
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {contact.phone}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {contact.message}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(contact.created_at)}
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedContact(contact)}
                      >
                        <Eye size={14} />
                      </Button>
                      {contact.status === 'new' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateStatus(contact.id, 'read')}
                          title="Mark as Read"
                        >
                          <CheckCircle size={14} />
                        </Button>
                      )}
                      {contact.status !== 'archived' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateStatus(contact.id, 'archived')}
                          title="Archive"
                        >
                          <Archive size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Detail Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contact Message</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold text-lg">
                  {selectedContact.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedContact.name}</p>
                  <p className="text-sm text-gray-500">{formatDate(selectedContact.created_at)}</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <Mail size={16} className="text-gray-400" />
                  <a href={`mailto:${selectedContact.email}`} className="text-purple-600 hover:underline">
                    {selectedContact.email}
                  </a>
                </p>
                {selectedContact.phone && (
                  <p className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    <a href={`tel:${selectedContact.phone}`} className="text-purple-600 hover:underline">
                      {selectedContact.phone}
                    </a>
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => updateStatus(selectedContact.id, 'read')}
                  variant="outline"
                  className="flex-1"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Mark as Read
                </Button>
                <Button
                  onClick={() => updateStatus(selectedContact.id, 'replied')}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  <Mail size={16} className="mr-2" />
                  Mark as Replied
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContacts;
